import clsx from 'clsx'
import { useMemo, useState } from 'react'
import { KpiCard, KpiStrip } from '../../../cc'
import { ReportTable, ReportTablePagination } from '../../../cc/ReportTable'
import type { Tone } from '../../../cc/tokens'
import { RankedBars } from '../../charts/RankedBars'
import { reportSegment, reportSegmentBtn } from '../../ui'
import { useReportTableState } from '../../useReportTableState'
import { toApiParams, type AlertFilters } from '../alertFilters'
import { fmtDec, fmtInt, kindLabel, pct, plural } from '../alertLabels'
import type { ByDriverRow, ByVehicleRow } from '../alertTypes'
import { useAlertAnalytics } from '../useAlertAnalytics'
import { AlertDrillList } from './AlertDrillList'
import { asRows, ClickHint, columnDefs, makeExport, useViewExport, ViewCard, ViewState, type ViewProps } from './viewKit'

/**
 * Drivers and Vehicles are the same report over a different key: one row per entity,
 * alert counts, night share, km-normalised rate, top kind. One implementation, two thin
 * wrappers, so a fix to the rate toggle or the cross-filter lands in both.
 */

type EntityRow = ByDriverRow | ByVehicleRow
type RankMetric = 'alerts' | 'alerts_per_100km'

export interface EntityRankConfig<T extends EntityRow> {
  path: '/by-driver' | '/by-vehicle'
  /** The column holding the entity's display id (driver_label / vehicle_id). */
  idColumn: keyof T & string
  /** Which AlertFilters field a row click sets. */
  filterKey: 'driver' | 'vehicle'
  noun: { one: string; many: string }
  columns: readonly string[]
  tableId: string
  exportName: string
  title: string
  /** "n vehicles" for a driver, "n drivers" for a vehicle. */
  spreadColumn: 'vehicles' | 'drivers'
  noData: { title: string; body: string }
}

/** vehicles for a driver row, drivers for a vehicle row -- the union has one or the other. */
function spreadOf(r: EntityRow, col: 'vehicles' | 'drivers'): number {
  return col === 'vehicles' ? ('vehicles' in r ? r.vehicles : 0) : ('drivers' in r ? r.drivers : 0)
}

function toneForRow(r: EntityRow): Tone {
  if (r.alerts === 0) return 'neutral'
  const critShare = r.critical / r.alerts
  if (critShare > 0.5) return 'danger'
  if (critShare > 0.2) return 'warn'
  return 'neutral'
}

export function EntityRankView<T extends EntityRow>({
  filters, setFilters, config,
}: ViewProps & { config: EntityRankConfig<T> }): JSX.Element {
  const params = useMemo(() => toApiParams(filters), [filters])
  const { rows, status, error } = useAlertAnalytics<T>(config.path, params)
  const [metric, setMetric] = useState<RankMetric>('alerts')

  const totals = useMemo(() => {
    const alerts = rows.reduce((s, r) => s + r.alerts, 0)
    const critical = rows.reduce((s, r) => s + r.critical, 0)
    const night = rows.reduce((s, r) => s + r.night_alerts, 0)
    const withKm = rows.filter((r) => r.alerts_per_100km !== null)
    const worstRate = withKm.length ? Math.max(...withKm.map((r) => r.alerts_per_100km ?? 0)) : null
    const km = rows.reduce((s, r) => s + (r.km ?? 0), 0)
    return { alerts, critical, night, worstRate, km, withKm: withKm.length }
  }, [rows])

  const idOf = (r: T): string => String(r[config.idColumn])
  const activeId = filters[config.filterKey]

  const ranked = useMemo(() => {
    const pool = metric === 'alerts' ? rows : rows.filter((r) => r.alerts_per_100km !== null)
    return [...pool]
      .sort((a, b) => (metric === 'alerts' ? b.alerts - a.alerts : (b.alerts_per_100km ?? 0) - (a.alerts_per_100km ?? 0)))
      .slice(0, 10)
      .map((r) => ({
        label: idOf(r),
        value: metric === 'alerts' ? r.alerts : (r.alerts_per_100km ?? 0),
        sub: `${plural(spreadOf(r, config.spreadColumn), config.spreadColumn.replace(/s$/, ''))} · ${fmtInt(r.critical)} critical · ${fmtInt(r.night_alerts)} at night · top ${kindLabel(r.top_kind)}`,
        tone: toneForRow(r),
        active: activeId === idOf(r),
        onClick: () => setFilters({ [config.filterKey]: activeId === idOf(r) ? '' : idOf(r) } as Partial<AlertFilters>),
      }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, metric, activeId])

  const table = useReportTableState(config.tableId, asRows(rows))
  const defs = useMemo(() => columnDefs(config.columns), [config.columns])
  const exportFn = useMemo(
    () => makeExport(rows, [...config.columns], config.exportName, config.title, filters, ['first_ts', 'last_ts']),
    [rows, config, filters],
  )
  useViewExport(exportFn)

  return (
    <ViewState status={status} error={error} rowCount={rows.length} filters={filters} setFilters={setFilters} noData={config.noData}>
      <div className="space-y-4">
        <KpiStrip>
          <KpiCard label={`${config.noun.many} with alerts`} value={rows.length} />
          <KpiCard label="Alerts" value={totals.alerts} />
          <KpiCard label="Critical" value={totals.critical} tone={totals.critical > 0 ? 'danger' : 'neutral'} dot={totals.critical > 0} sub={`${pct(totals.critical, totals.alerts)}% of alerts`} />
          <KpiCard label="At night" value={pct(totals.night, totals.alerts)} unit="%" sub="22:00–06:00 IST" tone={pct(totals.night, totals.alerts) > 40 ? 'warn' : 'neutral'} />
          <KpiCard
            label="Worst rate"
            value={totals.worstRate === null ? '—' : fmtDec(totals.worstRate)}
            unit={totals.worstRate === null ? undefined : '/ 100 km'}
            sub={totals.worstRate === null ? 'no trip km recorded for this site' : `${fmtInt(totals.withKm)} ${config.noun.many} with ≥ 25 km`}
          />
        </KpiStrip>

        <ViewCard
          title={`Top ${config.noun.many}`}
          sub={metric === 'alerts' ? 'By alert count. Someone who drove twice as far gets twice the chances -- check the per-100 km view too.' : 'Per 100 km of completed trips, so distance stops flattering the quiet ones. Needs ≥ 25 km in the window.'}
          actions={(
            <div className={reportSegment} role="group" aria-label="Ranking metric">
              {([['alerts', 'Alerts'], ['alerts_per_100km', 'Per 100 km']] as const).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  aria-pressed={metric === id}
                  disabled={id === 'alerts_per_100km' && totals.withKm === 0}
                  onClick={() => setMetric(id)}
                  className={clsx(reportSegmentBtn(metric === id), '!px-2.5 !py-1 !text-[11.5px] disabled:opacity-40')}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        >
          <ClickHint>Click a {config.noun.one} to focus every tab on it; click again to release.</ClickHint>
          <RankedBars
            items={ranked}
            formatValue={metric === 'alerts' ? undefined : (v) => fmtDec(v)}
            ariaLabel={`Top ${config.noun.many} by ${metric === 'alerts' ? 'alert count' : 'alerts per 100 km'}`}
          />
        </ViewCard>

        <AlertDrillList
          filters={filters}
          setFilters={setFilters}
          title={activeId ? `Alerts for ${activeId}` : 'Latest alerts'}
          idleHint={`Showing every ${config.noun.one}. Click a ${config.noun.one} above to see only their alerts.`}
        />

        <ViewCard title={`Every ${config.noun.one}`} sub="Click a column to sort; click a row to focus on it." bodyClassName="p-0">
          <ReportTable
            columns={[...config.columns]}
            rows={table.pageRows}
            columnDefs={defs}
            sort={table.sort}
            onSort={table.toggleSort}
            onRowClick={(row) => {
              const id = String(row[config.idColumn])
              setFilters({ [config.filterKey]: activeId === id ? '' : id } as Partial<AlertFilters>)
            }}
            rowOffset={table.page * (table.pageSize || table.sorted.length)}
            compact
            applyLoadStateLabels={false}
            className="rounded-none ring-0 shadow-none"
          />
          {table.totalCount > table.pageSize && table.pageSize > 0 ? (
            <div className="border-t border-slate-200 p-3">
              <ReportTablePagination
                page={table.page}
                totalPages={table.totalPages}
                pageSize={table.pageSize}
                pageSizes={table.pageSizes}
                showingFrom={table.showingFrom}
                showingTo={table.showingTo}
                totalCount={table.totalCount}
                onPageChange={table.setPage}
                onPageSizeChange={(s) => { table.setPageSize(s); table.setPage(0) }}
              />
            </div>
          ) : null}
        </ViewCard>
      </div>
    </ViewState>
  )
}
